import { on } from '@ember/modifier';
import { guidFor } from '@ember/object/internals';
import Component from '@glimmer/component';
import FocusTrapper from 'ember-focus-trapper/components/focus-trapper';
import { modifier } from 'ember-modifier';
import CloseButton from 'garaje/components/close-button';
import ButtonGroup from 'garaje/components/ui/button-group';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';

interface DocumentDeleteConfirmationModalSignature {
  Args: {
    isOpen: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    isDeleting?: boolean;
    errorMessage?: string | null;
  };
  Element: HTMLDivElement;
}

/**
 * Presentational confirmation modal for removing a pre-registration document from
 * an invite. It owns no transport — the caller passes `@onConfirm`/`@onCancel`
 * callbacks and reflects in-flight (`@isDeleting`) and failure (`@errorMessage`)
 * state through the args.
 */
export default class DocumentDeleteConfirmationModal extends Component<DocumentDeleteConfirmationModalSignature> {
  // Unique per instance so the dialog's accessible name resolves even if two
  // instances ever coexist in the DOM.
  titleId = `${guidFor(this)}-title`;

  // Steer initial focus to the non-destructive Cancel action (WCAG 3.3.4). Cancel is the first
  // focusable child (CloseButton is rendered last, below, but absolutely positioned), so
  // FocusTrapper's deferred refocus lands on Cancel; this modifier focuses it synchronously too —
  // both agree on Cancel.
  focusCancel = modifier((element: Element) => {
    if (element instanceof HTMLElement) element.focus();
  });

  handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      this.args.onCancel();
    }
  };

  <template>
    {{#if @isOpen}}
      <ModalDialog
        @clickOutsideToClose={{false}}
        @targetAttachment="center"
        @translucentOverlay={{true}}
        @onClose={{@onCancel}}
        @containerClass="!max-w-md"
      >
        <FocusTrapper>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={{this.titleId}}
            class="relative p-8"
            data-test-document-delete-modal
            {{on "keydown" this.handleKeydown}}
            ...attributes
          >
            <h2 id={{this.titleId}} class="text-2xl text-carbon-70 mb-4">
              Remove document?
            </h2>

            <p class="text-carbon-60">
              This removes the document from the visitor's profile and every visit it's attached to.
            </p>

            {{#if @errorMessage}}
              <p role="alert" aria-live="assertive" class="mt-4 text-sm text-red-50" data-test-document-delete-error>
                {{@errorMessage}}
              </p>
            {{/if}}

            <div class="pt-8 flex justify-end">
              <ButtonGroup>
                <button
                  type="button"
                  class="btn btn--small btn--outline hover:bg-carbon-5"
                  disabled={{@isDeleting}}
                  data-test-document-delete-cancel
                  {{this.focusCancel}}
                  {{on "click" @onCancel}}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="btn btn--small btn--red-outline"
                  disabled={{@isDeleting}}
                  data-test-document-delete-confirm
                  {{on "click" @onConfirm}}
                >
                  {{if @isDeleting "Removing…" "Remove document"}}
                </button>
              </ButtonGroup>
            </div>

            {{! Rendered last so Cancel is the first focusable child (initial focus lands there);
                absolutely positioned, so DOM order doesn't change its visual top-right placement. }}
            <CloseButton
              class="right-4 top-4 !bg-white !p-0 w-10 h-10"
              aria-label="Close"
              data-test-document-delete-close
              @onClick={{@onCancel}}
            />
          </div>
        </FocusTrapper>
      </ModalDialog>
    {{/if}}
  </template>
}
